import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { formatBytes } from "@/lib/utils"
import type { DiskUsageSnapshot } from "@/types"

interface DiskUsageLineProps {
  data: DiskUsageSnapshot[]
  className?: string
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export function DiskUsageLine({ data, className }: DiskUsageLineProps) {
  const chartData = data.map((snapshot) => {
    const breakdown = snapshot.breakdown || {}
    
    // Calculate total apps usage
    const appsTotal = Array.isArray(breakdown.apps)
      ? breakdown.apps.reduce((sum: number, app: { size: number }) => sum + (app.size || 0), 0)
      : 0

    // Calculate total watched directories usage
    const watchedDirsTotal = Array.isArray(breakdown.watched_directories)
      ? breakdown.watched_directories.reduce((sum: number, dir: { size: number }) => sum + (dir.size || 0), 0)
      : 0

    // Get other used space
    const otherUsed = breakdown.other_used || 0

    return {
      date: formatDate(snapshot.created_at),
      apps: appsTotal,
      watchedDirs: watchedDirsTotal,
      other: otherUsed,
      available: snapshot.available_bytes || 0,
      fullDate: snapshot.created_at,
    }
  })

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis
            dataKey="date"
            stroke="hsl(var(--muted-foreground))"
            style={{ fontSize: "12px" }}
          />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            style={{ fontSize: "12px" }}
            tickFormatter={(value) => formatBytes(value)}
          />
          <Tooltip
            // @ts-expect-error - Recharts Formatter type is too strict, our formatBytes handles undefined
            formatter={(value: number | undefined, name: string) => {
              const labels: Record<string, string> = {
                apps: "Applications",
                watchedDirs: "Watched Directories",
                other: "Other Used",
                available: "Available",
              }
              return [formatBytes(value), labels[name] || name]
            }}
            labelStyle={{ color: "hsl(var(--foreground))" }}
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
            }}
          />
          <Legend
            formatter={(value) => {
              const labels: Record<string, string> = {
                apps: "Applications",
                watchedDirs: "Watched Directories",
                other: "Other Used",
                available: "Available",
              }
              return labels[value] || value
            }}
          />
          <Area
            type="monotone"
            dataKey="apps"
            stackId="1"
            stroke="#3b82f6"
            fill="#3b82f6"
            fillOpacity={0.6}
            name="apps"
          />
          <Area
            type="monotone"
            dataKey="watchedDirs"
            stackId="1"
            stroke="#8b5cf6"
            fill="#8b5cf6"
            fillOpacity={0.6}
            name="watchedDirs"
          />
          <Area
            type="monotone"
            dataKey="other"
            stackId="1"
            stroke="#6b7280"
            fill="#6b7280"
            fillOpacity={0.6}
            name="other"
          />
          <Area
            type="monotone"
            dataKey="available"
            stackId="1"
            stroke="#10b981"
            fill="#10b981"
            fillOpacity={0.6}
            name="available"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default DiskUsageLine
